import type { ReactElement } from "react"
import { useContext } from "react"
import ArrowLinkAtm from "@onestore/hel/dist/components/atoms/ArrowLinkAtm"
import type Theme from "@onestore/hel/dist/typings/theme"
import type { CtaElement } from "@gatsby-plugin-generic-page/fragments/ctaElement"
import { getCtaLinkFlatData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { replaceLinkValues } from "@gatsby-plugin-generic-page/helpers/linkParser"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { typeMatches } from "@onestore-graphql"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import SearchUrlParametersContext from "~/context/SearchUrlParametersContext"
import useModalState from "~/hooks/useModalState"
import { sendGaEvent } from "~/lib/analytics"
import { getAnchorProperties } from "~/lib/dom"
import isEmpty from "~/lib/isEmpty"
import CtaModal from "../CtaModal"
import FormLink from "../FormLink"

interface Props {
  link: CtaElement
  typography?: Theme.Typography
}

function ArrowLink({ link, typography }: Props): ReactElement | null {
  const { currentPriceType } = usePriceTypeContext()
  const {
    actionTypeName,
    text,
    linkType,
    title,
    gaEvent,
    anchorId,
    form,
    href,
    openInNewTab,
    pages,
    modalTitle,
    texts,
  } = getCtaLinkFlatData(link, currentPriceType)
  const { isOpen, open, close } = useModalState(gaEvent)
  const currentSearchUrlParameters = useContext(SearchUrlParametersContext)

  if (isEmpty(actionTypeName)) {
    return null
  }

  const commonProps = {
    typography,
    title,
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.SCROLL_TO_SECTION) &&
    !isEmpty(anchorId)
  ) {
    const { onClick, href } = getAnchorProperties(anchorId)

    return (
      <ArrowLinkAtm
        {...commonProps}
        onClick={(event) => {
          onClick(event)
          sendGaEvent(gaEvent)
        }}
        href={href}
      >
        {text}
      </ArrowLinkAtm>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.OPEN_FORM) &&
    !isEmpty(form)
  ) {
    return (
      <FormLink {...commonProps} text={text} form={form} linkType={linkType} />
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.OPEN_MODAL) &&
    !isEmpty(texts)
  ) {
    return (
      <>
        <ArrowLinkAtm {...commonProps} onClick={open}>
          {text}
        </ArrowLinkAtm>

        <CtaModal
          isOpen={isOpen}
          texts={texts}
          title={modalTitle}
          onCloseButtonClick={close}
        />
      </>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.INTERNAL_LINK) &&
    !isEmpty(pages)
  ) {
    return (
      <ArrowLinkAtm
        {...commonProps}
        onClick={() => sendGaEvent(gaEvent)}
        to={pages[0].flatData.url}
      >
        {text}
      </ArrowLinkAtm>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.EXTERNAL_LINK) &&
    !isEmpty(href)
  ) {
    return (
      <ArrowLinkAtm
        {...commonProps}
        onClick={() => sendGaEvent(gaEvent)}
        href={replaceLinkValues(href, currentSearchUrlParameters)}
        openInNewTab={openInNewTab}
      >
        {text}
      </ArrowLinkAtm>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.CTA_AX_LINK) &&
    !isEmpty(href)
  ) {
    return (
      <ArrowLinkAtm
        {...commonProps}
        onClick={() => sendGaEvent(gaEvent)}
        href={replaceLinkValues(href, currentSearchUrlParameters)}
      >
        {text}
      </ArrowLinkAtm>
    )
  }

  return null
}

export default ArrowLink
